import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import PageHero from "@/components/layout/PageHero";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { Plane, ArrowRight, Clock } from "lucide-react";

export default function Flights() {
  const [flights, setFlights] = useState([]);
  const [airlines, setAirlines] = useState([]);
  useEffect(() => {
    api.get("/flights").then(({ data }) => setFlights(data)).catch(() => {});
    api.get("/airlines").then(({ data }) => setAirlines(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="flights-page">
      <PageHero
        kicker="Compare Flights"
        title="Find the cheapest flights to anywhere"
        subtitle="We search across 600+ airlines and online travel agents — including IndiGo, Air India, Emirates and Qatar Airways."
      />
      <div className="container-page py-10">
        <Breadcrumbs items={[{ label: "Flights" }]} />
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Airlines</div>
              <ul className="space-y-2">
                {airlines.map((a) => (
                  <li key={a.code} className="flex items-center gap-2 text-sm text-slate-700">
                    <img src={a.logo} alt={a.name} className="w-6 h-6 object-contain" />
                    {a.name}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-brand-50 border border-brand-100 rounded-xl p-5 text-sm">
              <div className="font-montserrat font-bold text-brand mb-1">Best Price Guarantee</div>
              <p className="text-slate-600">Found a lower fare? We'll match it. Affiliate disclosure applies — see footer.</p>
            </div>
          </aside>

          <div className="lg:col-span-3 space-y-4">
            <div className="text-sm text-slate-600 mb-2">{flights.length} flights · sorted by price</div>
            {flights.map((f, i) => (
              <a
                key={i}
                href={f.affiliate_url}
                target="_blank"
                rel="noopener sponsored"
                data-testid={`flight-${i}`}
                className="block bg-white border border-slate-200 rounded-2xl p-5 card-hover"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-3 md:w-44">
                    <div className="w-12 h-12 inline-flex items-center justify-center bg-slate-50 border border-slate-200 rounded-xl">
                      <Plane size={20} className="text-brand" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{f.airline}</div>
                      <div className="text-[11px] text-slate-500">{f.stops}</div>
                    </div>
                  </div>
                  <div className="flex-1 grid grid-cols-3 items-center text-center">
                    <div>
                      <div className="text-xl font-montserrat font-bold text-slate-900">{f.depart}</div>
                      <div className="text-xs text-slate-500">{f.from}</div>
                    </div>
                    <div className="text-xs text-slate-500">
                      <Clock size={12} className="inline -mt-0.5" /> {f.duration}
                      <div className="mt-1 h-px bg-slate-200" />
                    </div>
                    <div>
                      <div className="text-xl font-montserrat font-bold text-slate-900">{f.arrive}</div>
                      <div className="text-xs text-slate-500">{f.to}</div>
                    </div>
                  </div>
                  <div className="md:text-right md:w-44">
                    <div className="text-2xl font-montserrat font-extrabold text-slate-900">{fmtINR(f.price)}</div>
                    <div className="text-xs text-slate-500 mb-2">per person</div>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent">
                      View Deal <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
